import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminContentApi } from '../api/content';
import type {
  BannerUpsertDto,
  BlogPostUpsertDto,
  ContactMessageStatus,
  FaqItemUpsertDto,
  MenuItemDto,
  MenuItemUpsertDto,
  PageUpsertDto,
} from '../api/types';
import { contactMessageStatusValues } from '../api/types';
import { Column, DataTable, SearchInput, SelectFilter, Toolbar } from '../components/DataTable';
import { PageHeader } from '../components/Shell';
import {
  Badge,
  ConfirmDialog,
  Field,
  FormErrors,
  Modal,
  PageLoader,
  Pagination,
  Spinner,
} from '../components/ui';
import { GoogleSearchPreview } from '../components/GoogleSearchPreview';
import { RichTextEditor } from '../components/RichTextEditor';
import { ImageUploadField } from '../components/ImageUploadField';
import { useToast } from '../components/Toast';
import { useAuth } from '../context/AuthContext';
import { formatDate, formatDateTime, number, truncate, slugify } from '../lib/format';
import { contactStatusLabels, labelOf } from '../lib/labels';
import { P } from '../lib/permissions';

const TABS = [
  { key: 'sayfalar', label: 'Sayfalar' },
  { key: 'blog', label: 'Blog' },
  { key: 'bannerlar', label: 'Bannerlar' },
  { key: 'menuler', label: 'Menüler' },
  { key: 'sss', label: 'SSS' },
  { key: 'iletisim', label: 'İletişim mesajları' },
  { key: 'bulten', label: 'Bülten aboneleri' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

export function ContentPage() {
  const [tab, setTab] = useState<TabKey>('sayfalar');

  return (
    <div>
      <PageHeader title="İçerik" description="Sayfalar, blog, bannerlar, menüler ve iletişim" />

      <div className="mb-5 flex gap-1 overflow-x-auto border-b border-ink-200">
        {TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setTab(item.key)}
            className={`whitespace-nowrap border-b-2 px-4 py-2.5 text-sm transition ${
              tab === item.key
                ? 'border-ink-900 font-medium text-ink-900'
                : 'border-transparent text-ink-500 hover:text-ink-800'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'sayfalar' ? <PagesTab /> : null}
      {tab === 'blog' ? <BlogTab /> : null}
      {tab === 'bannerlar' ? <BannersTab /> : null}
      {tab === 'menuler' ? <MenusTab /> : null}
      {tab === 'sss' ? <FaqTab /> : null}
      {tab === 'iletisim' ? <ContactTab /> : null}
      {tab === 'bulten' ? <NewsletterTab /> : null}
    </div>
  );
}

/* ================================= SAYFALAR ================================= */

const emptyPage: PageUpsertDto = {
  title: '',
  slug: '',
  content: '',
  isPublished: false,
  showInFooter: true,
  displayOrder: 0,
  noIndex: false,
  seoTitle: '',
  seoDescription: '',
  seoKeywords: '',
};

function PagesTab() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const [editing, setEditing] = useState<{
    id: number | null;
    form: PageUpsertDto;
    isSlugCustomized?: boolean;
  } | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const pages = useQuery({
    queryKey: ['admin', 'pages'],
    queryFn: () => adminContentApi.pages(),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'pages'] });

  const save = useMutation({
    mutationFn: () =>
      editing!.id === null
        ? adminContentApi.createPage(editing!.form)
        : adminContentApi.updatePage(editing!.id, editing!.form),
    onSuccess: () => {
      toast.success('Sayfa kaydedildi.');
      setEditing(null);
      void invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => adminContentApi.deletePage(id),
    onSuccess: (message) => {
      toast.success(message ?? 'Sayfa silindi.');
      setDeleteId(null);
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'Sayfa silinemedi.'),
  });

  const columns: Column<NonNullable<typeof pages.data>[number]>[] = [
    {
      key: 'title',
      header: 'Sayfa',
      cell: (row) => (
        <div>
          <p className="font-medium text-ink-900">{row.title}</p>
          <p className="font-mono text-xs text-ink-400">/{row.slug}</p>
        </div>
      ),
    },
    {
      key: 'published',
      header: 'Durum',
      cell: (row) =>
        row.isPublished ? (
          <span className="badge bg-emerald-50 text-emerald-700">Yayında</span>
        ) : (
          <span className="badge bg-ink-100 text-ink-600">Taslak</span>
        ),
    },
    {
      key: 'footer',
      header: 'Alt bilgide',
      align: 'center',
      hideOnMobile: true,
      cell: (row) => (row.showInFooter ? 'Evet' : 'Hayır'),
    },
    {
      key: 'updated',
      header: 'Güncelleme',
      hideOnMobile: true,
      cell: (row) => <span className="text-ink-500">{formatDate(row.updatedAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (row) =>
        can(P.content.manage) ? (
          <div className="flex justify-end gap-1">
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={async () => {
                let content = row.content;
                if (!content) {
                  try {
                    const full = await adminContentApi.page(row.slug);
                    content = full.content;
                  } catch {
                    // ignore
                  }
                }
                setEditing({
                  id: row.id,
                  isSlugCustomized: true,
                  form: {
                    title: row.title,
                    slug: row.slug,
                    content: content || '',
                    isPublished: row.isPublished,
                    showInFooter: row.showInFooter,
                    displayOrder: row.displayOrder,
                    seoTitle: row.seoTitle,
                    seoDescription: row.seoDescription,
                    seoKeywords: row.seoKeywords,
                    noIndex: row.noIndex ?? false,
                  },
                });
              }}
            >
              Düzenle
            </button>
            {!row.isSystemPage ? (
              <button
                type="button"
                className="btn-ghost btn-sm text-red-600"
                onClick={() => setDeleteId(row.id)}
              >
                Sil
              </button>
            ) : null}
          </div>
        ) : null,
    },
  ];

  return (
    <div>
      {can(P.content.manage) ? (
        <div className="mb-4 flex justify-end">
          <button
            type="button"
            className="btn-primary"
            onClick={() => setEditing({ id: null, form: emptyPage, isSlugCustomized: false })}
          >
            Yeni sayfa
          </button>
        </div>
      ) : null}

      <DataTable
        columns={columns}
        rows={pages.data ?? []}
        rowKey={(row) => row.id}
        isLoading={pages.isLoading}
        error={pages.error}
        onRetry={() => void pages.refetch()}
        emptyMessage="Sayfa yok."
      />

      <Modal
        open={editing !== null}
        title={editing?.id === null ? 'Yeni sayfa' : 'Sayfayı düzenle'}
        onClose={() => setEditing(null)}
        size="xl"
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => save.mutate()}
              disabled={save.isPending || !editing?.form.title}
            >
              {save.isPending ? <Spinner className="h-4 w-4" /> : null}
              Kaydet
            </button>
          </>
        }
      >
        {editing ? (
          <div className="space-y-4">
            <FormErrors error={save.error} />

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Başlık" htmlFor="pg-title" required>
                <input
                  id="pg-title"
                  className="field"
                  value={editing.form.title}
                  onChange={(event) => {
                    const newTitle = event.target.value;
                    setEditing((c) => c && {
                      ...c,
                      form: {
                        ...c.form,
                        title: newTitle,
                        slug: !c.isSlugCustomized ? slugify(newTitle) : c.form.slug,
                      },
                    });
                  }}
                  placeholder="Örn: Gizlilik Politikası"
                />
              </Field>

              <Field
                label="URL adresi"
                htmlFor="pg-slug"
                hint={
                  <span className="flex items-center justify-between text-xs">
                    <span>{!editing.isSlugCustomized ? 'Başlıktan otomatik üretiliyor.' : 'Özel olarak düzenlendi.'}</span>
                    <button
                      type="button"
                      className="font-medium text-brand-600 hover:text-brand-700 hover:underline"
                      onClick={() =>
                        setEditing((c) => c && {
                          ...c,
                          isSlugCustomized: false,
                          form: { ...c.form, slug: slugify(c.form.title) },
                        })
                      }
                    >
                      Başlıktan yeniden üret
                    </button>
                  </span>
                }
              >
                <input
                  id="pg-slug"
                  className="field font-mono text-sm"
                  value={editing.form.slug ?? ''}
                  onChange={(event) =>
                    setEditing((c) => c && {
                      ...c,
                      isSlugCustomized: true,
                      form: { ...c.form, slug: event.target.value.toLowerCase().replace(/\s+/g, '-') },
                    })
                  }
                  onBlur={(event) => {
                    if (event.target.value) {
                      setEditing((c) => c && {
                        ...c,
                        form: { ...c.form, slug: slugify(event.target.value) },
                      });
                    }
                  }}
                  placeholder="gizlilik-politikasi"
                />
              </Field>
            </div>

            <Field
              label="İçerik"
              htmlFor="pg-content"
              required
              hint="Zengin metin editörü ile sayfayı biçimlendirebilir veya doğrudan HTML koduna geçebilirsiniz."
            >
              <RichTextEditor
                id="pg-content"
                value={editing.form.content}
                onChange={(content) =>
                  setEditing((c) => c && { ...c, form: { ...c.form, content } })
                }
                minHeight="min-h-[300px]"
                placeholder="Sayfa içeriğini buraya girin..."
              />
            </Field>

            {/* SEO Bölümü */}
            <div className="pt-4 border-t border-ink-200 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-ink-900">Arama Motoru Görünümü (SEO)</h3>
                <p className="mt-0.5 text-xs text-ink-500">
                  Sayfanın Google ve diğer arama motorlarında nasıl listeleneceğini özelleştirin.
                </p>
              </div>

              <GoogleSearchPreview
                title={editing.form.seoTitle}
                fallbackTitle={editing.form.title}
                description={editing.form.seoDescription}
                fallbackDescription={editing.form.content?.replace(/<[^>]*>/g, '').slice(0, 160)}
                urlPath={`sayfa/${editing.form.slug || 'sayfa-adi'}`}
                noIndex={editing.form.noIndex}
              />

              <div className="grid gap-4 sm:grid-cols-2">
                <Field label="SEO başlığı" htmlFor="pg-seo" hint="Boşsa sayfa başlığı kullanılır.">
                  <input
                    id="pg-seo"
                    className="field"
                    maxLength={200}
                    value={editing.form.seoTitle ?? ''}
                    onChange={(event) =>
                      setEditing((c) => c && { ...c, form: { ...c.form, seoTitle: event.target.value } })
                    }
                  />
                </Field>
                <Field label="Anahtar kelimeler" htmlFor="pg-keys" hint="Virgülle ayırın.">
                  <input
                    id="pg-keys"
                    className="field"
                    value={editing.form.seoKeywords ?? ''}
                    onChange={(event) =>
                      setEditing((c) => c && { ...c, form: { ...c.form, seoKeywords: event.target.value } })
                    }
                  />
                </Field>
              </div>

              <Field label="SEO açıklaması" htmlFor="pg-seodesc">
                <textarea
                  id="pg-seodesc"
                  className="field min-h-20"
                  maxLength={320}
                  value={editing.form.seoDescription ?? ''}
                  onChange={(event) =>
                    setEditing(
                      (c) => c && { ...c, form: { ...c.form, seoDescription: event.target.value } },
                    )
                  }
                />
              </Field>
            </div>

            <div className="flex flex-wrap gap-4">
              <Check
                label="Yayında"
                checked={editing.form.isPublished}
                onChange={(value) =>
                  setEditing((c) => c && { ...c, form: { ...c.form, isPublished: value } })
                }
              />
              <Check
                label="Alt bilgide göster"
                checked={editing.form.showInFooter}
                onChange={(value) =>
                  setEditing((c) => c && { ...c, form: { ...c.form, showInFooter: value } })
                }
              />
              <Check
                label="Arama motorlarına kapalı"
                checked={editing.form.noIndex}
                onChange={(value) =>
                  setEditing((c) => c && { ...c, form: { ...c.form, noIndex: value } })
                }
              />
            </div>
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={deleteId !== null}
        title="Sayfayı sil"
        message="Sayfa kalıcı olarak silinir."
        confirmLabel="Sil"
        danger
        busy={remove.isPending}
        onCancel={() => setDeleteId(null)}
        onConfirm={() => deleteId !== null && remove.mutate(deleteId)}
      />
    </div>
  );
}

function Check({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-2 text-sm text-ink-700">
      <input
        type="checkbox"
        className="h-4 w-4 rounded border-ink-300 text-brand-600"
        checked={checked}
        onChange={(event) => onChange(event.target.checked)}
      />
      {label}
    </label>
  );
}

/* =================================== BLOG =================================== */

const emptyPost: BlogPostUpsertDto = {
  title: '',
  slug: '',
  summary: '',
  content: '',
  isPublished: false,
  featuredProductIds: [],
  seoTitle: '',
  seoDescription: '',
};

function BlogTab() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<{
    id: number | null;
    form: BlogPostUpsertDto;
    isSlugCustomized?: boolean;
  } | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const posts = useQuery({
    queryKey: ['admin', 'blog', page],
    queryFn: () => adminContentApi.blogPosts({ page, pageSize: 20 }),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'blog'] });

  const save = useMutation({
    mutationFn: () =>
      editing!.id === null
        ? adminContentApi.createBlogPost(editing!.form)
        : adminContentApi.updateBlogPost(editing!.id, editing!.form),
    onSuccess: () => {
      toast.success('Yazı kaydedildi.');
      setEditing(null);
      void invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => adminContentApi.deleteBlogPost(id),
    onSuccess: (message) => {
      toast.success(message ?? 'Yazı silindi.');
      setDeleteId(null);
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'Yazı silinemedi.'),
  });

  const columns: Column<NonNullable<typeof posts.data>['items'][number]>[] = [
    {
      key: 'title',
      header: 'Yazı',
      cell: (row) => (
        <div className="flex items-center gap-3">
          <div className="h-10 w-14 shrink-0 overflow-hidden rounded bg-ink-50">
            {row.coverImageUrl ? (
              <img src={row.coverImageUrl} alt="" className="h-full w-full object-cover" />
            ) : null}
          </div>
          <div className="min-w-0">
            <p className="truncate font-medium text-ink-900">{row.title}</p>
            <p className="truncate font-mono text-xs text-ink-400">/{row.slug}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Kategori',
      hideOnMobile: true,
      cell: (row) => row.categoryName ?? <span className="text-ink-300">—</span>,
    },
    {
      key: 'views',
      header: 'Görüntülenme',
      align: 'right',
      hideOnMobile: true,
      cell: (row) => number(row.viewCount),
    },
    {
      key: 'published',
      header: 'Durum',
      cell: (row) =>
        row.isPublished ? (
          <span className="badge bg-emerald-50 text-emerald-700">Yayında</span>
        ) : (
          <span className="badge bg-ink-100 text-ink-600">Taslak</span>
        ),
    },
    {
      key: 'date',
      header: 'Tarih',
      hideOnMobile: true,
      cell: (row) => <span className="text-ink-500">{formatDate(row.publishedAt)}</span>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (row) =>
        can(P.content.manage) ? (
          <div className="flex justify-end gap-1">
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={async () => {
                let content = row.content;
                if (!content) {
                  try {
                    const full = await adminContentApi.blogPost(row.slug);
                    content = full.content;
                  } catch {
                    // ignore
                  }
                }
                setEditing({
                  id: row.id,
                  isSlugCustomized: true,
                  form: {
                    title: row.title,
                    slug: row.slug,
                    summary: row.summary,
                    content: content ?? '',
                    coverImageUrl: row.coverImageUrl,
                    authorName: row.authorName,
                    isPublished: row.isPublished,
                    publishedAt: row.publishedAt,
                    tags: row.tags,
                    categoryName: row.categoryName,
                    featuredProductIds: row.featuredProductIds,
                    seoTitle: row.seoTitle,
                    seoDescription: row.seoDescription,
                  },
                });
              }}
            >
              Düzenle
            </button>
            <button
              type="button"
              className="btn-ghost btn-sm text-red-600"
              onClick={() => setDeleteId(row.id)}
            >
              Sil
            </button>
          </div>
        ) : null,
    },
  ];

  return (
    <div>
      {can(P.content.manage) ? (
        <div className="mb-4 flex justify-end">
          <button
            type="button"
            className="btn-primary"
            onClick={() => setEditing({ id: null, form: emptyPost, isSlugCustomized: false })}
          >
            Yeni yazı
          </button>
        </div>
      ) : null}

      <DataTable
        columns={columns}
        rows={posts.data?.items ?? []}
        rowKey={(row) => row.id}
        isLoading={posts.isLoading}
        error={posts.error}
        onRetry={() => void posts.refetch()}
        emptyMessage="Blog yazısı yok."
        footer={
          posts.data ? (
            <Pagination
              page={posts.data.page}
              totalPages={posts.data.totalPages}
              totalCount={posts.data.totalCount}
              onChange={setPage}
            />
          ) : null
        }
      />

      <Modal
        open={editing !== null}
        title={editing?.id === null ? 'Yeni yazı' : 'Yazıyı düzenle'}
        onClose={() => setEditing(null)}
        size="xl"
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => save.mutate()}
              disabled={save.isPending || !editing?.form.title}
            >
              {save.isPending ? <Spinner className="h-4 w-4" /> : null}
              Kaydet
            </button>
          </>
        }
      >
        {editing ? (
          <div className="space-y-4">
            <FormErrors error={save.error} />

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Başlık" htmlFor="bp-title" required>
                <input
                  id="bp-title"
                  className="field"
                  value={editing.form.title}
                  onChange={(event) => {
                    const newTitle = event.target.value;
                    setEditing((c) => c && {
                      ...c,
                      form: {
                        ...c.form,
                        title: newTitle,
                        slug: !c.isSlugCustomized ? slugify(newTitle) : c.form.slug,
                      },
                    });
                  }}
                  placeholder="Örn: Hurmanın Faydaları Nelerdir?"
                />
              </Field>

              <Field
                label="URL adresi (slug)"
                htmlFor="bp-slug"
                hint={
                  <span className="flex items-center justify-between text-xs">
                    <span>{!editing.isSlugCustomized ? 'Başlıktan otomatik üretiliyor.' : 'Özel olarak düzenlendi.'}</span>
                    <button
                      type="button"
                      className="font-medium text-brand-600 hover:text-brand-700 hover:underline"
                      onClick={() =>
                        setEditing((c) => c && {
                          ...c,
                          isSlugCustomized: false,
                          form: { ...c.form, slug: slugify(c.form.title) },
                        })
                      }
                    >
                      Başlıktan yeniden üret
                    </button>
                  </span>
                }
              >
                <input
                  id="bp-slug"
                  className="field font-mono text-sm"
                  value={editing.form.slug ?? ''}
                  onChange={(event) =>
                    setEditing((c) => c && {
                      ...c,
                      isSlugCustomized: true,
                      form: { ...c.form, slug: event.target.value.toLowerCase().replace(/\s+/g, '-') },
                    })
                  }
                  onBlur={(event) => {
                    if (event.target.value) {
                      setEditing((c) => c && {
                        ...c,
                        form: { ...c.form, slug: slugify(event.target.value) },
                      });
                    }
                  }}
                  placeholder="hurmanin-faydalari-nelerdir"
                />
              </Field>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Kategori" htmlFor="bp-category">
                <input
                  id="bp-category"
                  className="field"
                  value={editing.form.categoryName ?? ''}
                  onChange={(event) =>
                    setEditing(
                      (c) => c && { ...c, form: { ...c.form, categoryName: event.target.value } },
                    )
                  }
                />
              </Field>

              <Field label="Yazar" htmlFor="bp-author">
                <input
                  id="bp-author"
                  className="field"
                  value={editing.form.authorName ?? ''}
                  onChange={(event) =>
                    setEditing(
                      (c) => c && { ...c, form: { ...c.form, authorName: event.target.value } },
                    )
                  }
                />
              </Field>

              <ImageUploadField
                label="Kapak görseli"
                className="sm:col-span-2"
                folder="blog"
                value={editing.form.coverImageUrl ?? ''}
                onChange={(url) =>
                  setEditing(
                    (c) => c && { ...c, form: { ...c.form, coverImageUrl: url } },
                  )
                }
                hint="Önerilen boyut: 1200x630 piksel"
              />
            </div>

            <Field label="Özet" htmlFor="bp-summary">
              <textarea
                id="bp-summary"
                className="field min-h-20"
                value={editing.form.summary ?? ''}
                onChange={(event) =>
                  setEditing((c) => c && { ...c, form: { ...c.form, summary: event.target.value } })
                }
              />
            </Field>

            <Field
              label="İçerik"
              htmlFor="bp-content"
              required
              hint="Zengin metin editörü ile blog yazınızı biçimlendirin."
            >
              <RichTextEditor
                id="bp-content"
                value={editing.form.content}
                onChange={(content) =>
                  setEditing((c) => c && { ...c, form: { ...c.form, content } })
                }
                minHeight="min-h-[300px]"
                placeholder="Blog yazısı içeriğini buraya girin..."
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Etiketler" htmlFor="bp-tags" hint="Virgülle ayırın.">
                <input
                  id="bp-tags"
                  className="field"
                  value={editing.form.tags ?? ''}
                  onChange={(event) =>
                    setEditing((c) => c && { ...c, form: { ...c.form, tags: event.target.value } })
                  }
                />
              </Field>

              <Field
                label="Öne çıkan ürün kimlikleri"
                htmlFor="bp-products"
                hint="Virgülle ayırın."
              >
                <input
                  id="bp-products"
                  className="field font-mono text-sm"
                  value={editing.form.featuredProductIds.join(', ')}
                  onChange={(event) =>
                    setEditing(
                      (c) =>
                        c && {
                          ...c,
                          form: {
                            ...c.form,
                            featuredProductIds: event.target.value
                              .split(',')
                              .map((raw) => Number(raw.trim()))
                              .filter((value) => Number.isFinite(value) && value > 0),
                          },
                        },
                    )
                  }
                />
              </Field>
            </div>

            <Check
              label="Yayında"
              checked={editing.form.isPublished}
              onChange={(value) =>
                setEditing((c) => c && { ...c, form: { ...c.form, isPublished: value } })
              }
            />

            {/* SEO Bölümü */}
            <div className="pt-4 border-t border-ink-200 space-y-4">
              <div>
                <h3 className="text-sm font-semibold text-ink-900">Arama Motoru Görünümü (SEO)</h3>
                <p className="mt-0.5 text-xs text-ink-500">
                  Blog yazısının Google ve diğer arama motorlarında nasıl listeleneceğini özelleştirin.
                </p>
              </div>

              <GoogleSearchPreview
                title={editing.form.seoTitle}
                fallbackTitle={editing.form.title}
                description={editing.form.seoDescription}
                fallbackDescription={editing.form.summary || editing.form.content?.replace(/<[^>]*>/g, '').slice(0, 160)}
                urlPath={`blog/${editing.form.slug || 'yazi-adi'}`}
              />

              <Field label="SEO başlığı" htmlFor="bp-seo-title" hint="Boşsa yazı başlığı kullanılır.">
                <input
                  id="bp-seo-title"
                  className="field"
                  maxLength={200}
                  value={editing.form.seoTitle ?? ''}
                  onChange={(event) =>
                    setEditing((c) => c && { ...c, form: { ...c.form, seoTitle: event.target.value } })
                  }
                />
              </Field>

              <Field label="SEO açıklaması" htmlFor="bp-seo-desc">
                <textarea
                  id="bp-seo-desc"
                  className="field min-h-20"
                  maxLength={320}
                  value={editing.form.seoDescription ?? ''}
                  onChange={(event) =>
                    setEditing(
                      (c) => c && { ...c, form: { ...c.form, seoDescription: event.target.value } },
                    )
                  }
                />
              </Field>
            </div>
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={deleteId !== null}
        title="Yazıyı sil"
        message="Blog yazısı kalıcı olarak silinir."
        confirmLabel="Sil"
        danger
        busy={remove.isPending}
        onCancel={() => setDeleteId(null)}
        onConfirm={() => deleteId !== null && remove.mutate(deleteId)}
      />
    </div>
  );
}

/* ================================= BANNERLAR ================================= */

const BANNER_PLACEMENTS = [
  { value: 'home-hero', label: 'Ana sayfa üst (Hero Slider)' },
  { value: 'home-promo', label: 'Ana sayfa ikincil (Kampanya Bannerı)' },
  { value: 'category', label: 'Kategori sayfası (Sayfa Üstü)' },
  { value: 'sidebar', label: 'Yan panel (Kenar Çubuğu)' },
];

const BANNER_PLACEMENT_LABELS: Record<string, string> = {
  'home-hero': 'Ana Sayfa Üst (Hero)',
  'home-promo': 'Ana Sayfa İkincil',
  'home-secondary': 'Ana Sayfa İkincil',
  'category': 'Kategori Sayfası',
  'sidebar': 'Yan Panel',
};

interface BannerExtraMeta {
  b: string;
  b2: string;
  f1: string;
  f2: string;
}

function parseBannerExtra(
  altText?: string | null,
  title?: string,
  placement?: string,
): BannerExtraMeta {
  if (altText && altText.trim().startsWith('{')) {
    try {
      const parsed = JSON.parse(altText) as Partial<BannerExtraMeta>;
      return {
        b: parsed.b ?? '',
        b2: parsed.b2 ?? '',
        f1: parsed.f1 ?? '',
        f2: parsed.f2 ?? '',
      };
    } catch {
      // Fallback below
    }
  }
  const isPromo = placement === 'home-promo' || placement === 'home-secondary';
  const defaultBadge = isPromo ? 'ÖZEL FIRSAT' : 'ÖZEL SEZON KAMPANYASI';
  return {
    b: altText && altText !== title ? altText : defaultBadge,
    b2: 'Tüm Ürünleri İncele',
    f1: 'Kutsal Topraklardan %100 Orijinal',
    f2: '₺1.500,00 Üzeri Ücretsiz Kargo',
  };
}

const emptyBanner: BannerUpsertDto = {
  title: '',
  imageUrl: '',
  placement: 'home-hero',
  isActive: true,
  displayOrder: 0,
  altText: JSON.stringify({
    b: 'ÖZEL SEZON KAMPANYASI',
    b2: 'Tüm Ürünleri İncele',
    f1: 'Kutsal Topraklardan %100 Orijinal',
    f2: '₺1.500,00 Üzeri Ücretsiz Kargo',
  }),
};

function BannersTab() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const [placement, setPlacement] = useState('');
  const [editing, setEditing] = useState<{ id: number | null; form: BannerUpsertDto } | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const banners = useQuery({
    queryKey: ['admin', 'banners', placement],
    queryFn: () => adminContentApi.banners(placement || undefined),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'banners'] });
    queryClient.invalidateQueries({ queryKey: ['content', 'banners'] });
  };

  const save = useMutation({
    mutationFn: () =>
      editing!.id === null
        ? adminContentApi.createBanner(editing!.form)
        : adminContentApi.updateBanner(editing!.id, editing!.form),
    onSuccess: () => {
      toast.success('Banner kaydedildi.');
      setEditing(null);
      void invalidate();
      void banners.refetch();
    },
    onError: (error) => toast.fromError(error, 'Banner kaydedilemedi.'),
  });

  const remove = useMutation({
    mutationFn: (id: number) => adminContentApi.deleteBanner(id),
    onSuccess: (message) => {
      toast.success(message ?? 'Banner silindi.');
      setDeleteId(null);
      void invalidate();
      void banners.refetch();
    },
    onError: (error) => toast.fromError(error, 'Banner silinemedi.'),
  });

  if (banners.isLoading) return <PageLoader />;

  const currentExtra = editing
    ? parseBannerExtra(editing.form.altText, editing.form.title, editing.form.placement)
    : null;

  const updateExtra = (patch: Partial<BannerExtraMeta>) => {
    setEditing((c) => {
      if (!c) return c;
      const prev = parseBannerExtra(c.form.altText, c.form.title, c.form.placement);
      const next = { ...prev, ...patch };
      return {
        ...c,
        form: {
          ...c.form,
          altText: JSON.stringify(next),
        },
      };
    });
  };

  return (
    <div>
      <Toolbar>
        <SelectFilter
          label="Yerleşim"
          value={placement === 'home-secondary' ? 'home-promo' : placement}
          onChange={setPlacement}
          options={BANNER_PLACEMENTS}
        />

        {can(P.content.manage) ? (
          <button
            type="button"
            className="btn-primary ml-auto"
            onClick={() => setEditing({ id: null, form: emptyBanner })}
          >
            + Yeni Banner Ekle
          </button>
        ) : null}
      </Toolbar>

      {(banners.data ?? []).length === 0 ? (
        <p className="rounded-xl border border-dashed border-ink-200 px-6 py-12 text-center text-sm text-ink-500">
          Bu yerleşimde kayıtlı banner bulunamadı.
        </p>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {(banners.data ?? []).map((banner) => (
            <li key={banner.id} className="card overflow-hidden">
              <div className="aspect-[16/9] bg-ink-100">
                {banner.imageUrl ? (
                  <img src={banner.imageUrl} alt="" className="h-full w-full object-cover" />
                ) : null}
              </div>
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-ink-900">{banner.title}</p>
                    {banner.subtitle ? (
                      <p className="truncate text-xs text-ink-500">{banner.subtitle}</p>
                    ) : null}
                  </div>
                  {banner.isActive ? (
                    <span className="badge bg-emerald-50 text-emerald-700">Aktif</span>
                  ) : (
                    <span className="badge bg-ink-100 text-ink-600">Pasif</span>
                  )}
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center rounded-md bg-brand-50 border border-brand-200/60 px-2 py-0.5 text-xs font-semibold text-brand-800">
                    {BANNER_PLACEMENT_LABELS[banner.placement] ?? banner.placement}
                  </span>
                  {(banner.startsAt || banner.endsAt) ? (
                    <span className="text-xs text-ink-400">
                      {banner.startsAt ? formatDate(banner.startsAt) : ''}
                      {banner.endsAt ? ` – ${formatDate(banner.endsAt)}` : ''}
                    </span>
                  ) : null}
                </div>

                {can(P.content.manage) ? (
                  <div className="mt-3 flex gap-1">
                    <button
                      type="button"
                      className="btn-secondary btn-sm"
                      onClick={() => {
                        const normalizedPlacement =
                          banner.placement === 'home-secondary' ? 'home-promo' : banner.placement;
                        const isHeroOrPromo =
                          normalizedPlacement === 'home-hero' || normalizedPlacement === 'home-promo';
                        const normalizedAlt = isHeroOrPromo
                          ? JSON.stringify(
                              parseBannerExtra(banner.altText, banner.title, normalizedPlacement),
                            )
                          : banner.altText;
                        setEditing({
                          id: banner.id,
                          form: {
                            title: banner.title,
                            subtitle: banner.subtitle,
                            imageUrl: banner.imageUrl,
                            mobileImageUrl: banner.mobileImageUrl,
                            linkUrl: banner.linkUrl,
                            buttonText: banner.buttonText,
                            placement: normalizedPlacement,
                            isActive: banner.isActive,
                            displayOrder: banner.displayOrder,
                            startsAt: banner.startsAt,
                            endsAt: banner.endsAt,
                            altText: normalizedAlt,
                          },
                        });
                      }}
                    >
                      Düzenle
                    </button>
                    <button
                      type="button"
                      className="btn-ghost btn-sm text-red-600"
                      onClick={() => setDeleteId(banner.id)}
                    >
                      Sil
                    </button>
                  </div>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={editing !== null}
        title={editing?.id === null ? 'Yeni banner' : 'Banner düzenle'}
        onClose={() => setEditing(null)}
        size="lg"
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => save.mutate()}
              disabled={save.isPending || !editing?.form.title || !editing?.form.imageUrl}
            >
              {save.isPending ? <Spinner className="h-4 w-4" /> : null}
              Kaydet
            </button>
          </>
        }
      >
        {editing ? (
          <div className="space-y-4">
            <FormErrors error={save.error} />

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Başlık" htmlFor="bn-title" required>
                <input
                  id="bn-title"
                  className="field"
                  value={editing.form.title}
                  onChange={(event) =>
                    setEditing((c) => c && { ...c, form: { ...c.form, title: event.target.value } })
                  }
                />
              </Field>

              <Field label="Alt başlık" htmlFor="bn-subtitle">
                <input
                  id="bn-subtitle"
                  className="field"
                  value={editing.form.subtitle ?? ''}
                  onChange={(event) =>
                    setEditing((c) => c && { ...c, form: { ...c.form, subtitle: event.target.value } })
                  }
                />
              </Field>

              <ImageUploadField
                label="Masaüstü görseli"
                className="sm:col-span-2"
                folder="banners"
                required
                aspectRatioText="Örn. 1920x600 piksel"
                value={editing.form.imageUrl}
                onChange={(url) =>
                  setEditing((c) => c && { ...c, form: { ...c.form, imageUrl: url } })
                }
              />

              <ImageUploadField
                label="Mobil görsel (İsteğe bağlı)"
                className="sm:col-span-2"
                folder="banners"
                aspectRatioText="Örn. 768x800 piksel"
                value={editing.form.mobileImageUrl ?? ''}
                onChange={(url) =>
                  setEditing(
                    (c) => c && { ...c, form: { ...c.form, mobileImageUrl: url } },
                  )
                }
              />

              <Field label="Bağlantı adresi" htmlFor="bn-link">
                <input
                  id="bn-link"
                  className="field"
                  placeholder="/urunler?sale=1"
                  value={editing.form.linkUrl ?? ''}
                  onChange={(event) =>
                    setEditing((c) => c && { ...c, form: { ...c.form, linkUrl: event.target.value } })
                  }
                />
              </Field>

              <Field label="Birincil düğme metni" htmlFor="bn-button" hint="Örn: Alışverişe Başla">
                <input
                  id="bn-button"
                  className="field"
                  placeholder="Alışverişe Başla"
                  value={editing.form.buttonText ?? ''}
                  onChange={(event) =>
                    setEditing(
                      (c) => c && { ...c, form: { ...c.form, buttonText: event.target.value } },
                    )
                  }
                />
              </Field>

              <Field
                label="Yerleşim"
                htmlFor="bn-placement"
                required
                hint="Bannerın web sitesinde görüntüleneceği konumu belirler."
              >
                <select
                  id="bn-placement"
                  className="field"
                  value={editing.form.placement === 'home-secondary' ? 'home-promo' : editing.form.placement}
                  onChange={(event) =>
                    setEditing(
                      (c) => c && { ...c, form: { ...c.form, placement: event.target.value } },
                    )
                  }
                >
                  {BANNER_PLACEMENTS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </Field>

              {editing.form.placement === 'home-hero' || editing.form.placement === 'home-promo' ? (
                <Field
                  label="Üst Rozet / Kampanya Etiketi"
                  htmlFor="bn-badge"
                  hint="Boş bırakırsanız rozet gizlenir."
                >
                  <input
                    id="bn-badge"
                    className="field"
                    maxLength={50}
                    placeholder="Örn: ÖZEL SEZON KAMPANYASI"
                    value={currentExtra?.b ?? ''}
                    onChange={(event) => updateExtra({ b: event.target.value })}
                  />
                </Field>
              ) : (
                <Field label="Alternatif metin" htmlFor="bn-alt">
                  <input
                    id="bn-alt"
                    className="field"
                    value={editing.form.altText ?? ''}
                    onChange={(event) =>
                      setEditing((c) => c && { ...c, form: { ...c.form, altText: event.target.value } })
                    }
                  />
                </Field>
              )}

              {editing.form.placement === 'home-hero' ? (
                <>
                  <Field
                    label="İkinci Düğme Metni"
                    htmlFor="bn-btn2"
                    hint="Boş bırakırsanız ikinci düğme gizlenir."
                  >
                    <input
                      id="bn-btn2"
                      className="field"
                      maxLength={40}
                      placeholder="Örn: Tüm Ürünleri İncele"
                      value={currentExtra?.b2 ?? ''}
                      onChange={(event) => updateExtra({ b2: event.target.value })}
                    />
                  </Field>

                  <Field
                    label="Alt Tikli Bilgi 1 (Sol)"
                    htmlFor="bn-f1"
                    hint="Boş bırakırsanız bu bilgi maddesi gizlenir."
                  >
                    <input
                      id="bn-f1"
                      className="field"
                      maxLength={55}
                      placeholder="Örn: Kutsal Topraklardan %100 Orijinal"
                      value={currentExtra?.f1 ?? ''}
                      onChange={(event) => updateExtra({ f1: event.target.value })}
                    />
                  </Field>

                  <Field
                    label="Alt Tikli Bilgi 2 (Sağ)"
                    htmlFor="bn-f2"
                    hint="Boş bırakırsanız bu bilgi maddesi gizlenir."
                  >
                    <input
                      id="bn-f2"
                      className="field"
                      maxLength={55}
                      placeholder="Örn: ₺1.500,00 Üzeri Ücretsiz Kargo"
                      value={currentExtra?.f2 ?? ''}
                      onChange={(event) => updateExtra({ f2: event.target.value })}
                    />
                  </Field>
                </>
              ) : null}

              <Field label="Başlangıç" htmlFor="bn-start">
                <input
                  id="bn-start"
                  type="date"
                  className="field"
                  value={editing.form.startsAt?.slice(0, 10) ?? ''}
                  onChange={(event) =>
                    setEditing(
                      (c) =>
                        c && {
                          ...c,
                          form: {
                            ...c.form,
                            startsAt: event.target.value
                              ? new Date(event.target.value).toISOString()
                              : null,
                          },
                        },
                    )
                  }
                />
              </Field>

              <Field label="Bitiş" htmlFor="bn-end">
                <input
                  id="bn-end"
                  type="date"
                  className="field"
                  value={editing.form.endsAt?.slice(0, 10) ?? ''}
                  onChange={(event) =>
                    setEditing(
                      (c) =>
                        c && {
                          ...c,
                          form: {
                            ...c.form,
                            endsAt: event.target.value
                              ? new Date(event.target.value).toISOString()
                              : null,
                          },
                        },
                    )
                  }
                />
              </Field>
            </div>

            <Check
              label="Aktif"
              checked={editing.form.isActive}
              onChange={(value) =>
                setEditing((c) => c && { ...c, form: { ...c.form, isActive: value } })
              }
            />
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={deleteId !== null}
        title="Banner sil"
        message="Banner kalıcı olarak silinir."
        confirmLabel="Sil"
        danger
        busy={remove.isPending}
        onCancel={() => setDeleteId(null)}
        onConfirm={() => deleteId !== null && remove.mutate(deleteId)}
      />
    </div>
  );
}

/* ================================== MENULER ================================== */

function MenusTab() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const [menuKey, setMenuKey] = useState('main');
  const [editing, setEditing] = useState<{ id: number | null; form: MenuItemUpsertDto } | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const menu = useQuery({
    queryKey: ['admin', 'menu', menuKey],
    queryFn: () => adminContentApi.menu(menuKey),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'menu'] });
    queryClient.invalidateQueries({ queryKey: ['content', 'menu'] });
  };

  const save = useMutation({
    mutationFn: () =>
      editing!.id === null
        ? adminContentApi.createMenuItem(editing!.form)
        : adminContentApi.updateMenuItem(editing!.id, editing!.form),
    onSuccess: () => {
      toast.success('Menü öğesi kaydedildi.');
      setEditing(null);
      void invalidate();
      void menu.refetch();
    },
    onError: (error) => toast.fromError(error, 'Kaydedilemedi.'),
  });

  const remove = useMutation({
    mutationFn: (id: number) => adminContentApi.deleteMenuItem(id),
    onSuccess: (message) => {
      toast.success(message ?? 'Menü öğesi silindi.');
      setDeleteId(null);
      void invalidate();
      void menu.refetch();
    },
    onError: (error) => toast.fromError(error, 'Silinemedi.'),
  });

  // Hiyerarşik düz liste (üst menü seçimi için)
  const getFlatParents = (
    items: MenuItemDto[],
    depth = 0,
    excludeId: number | null = null,
  ): Array<{ id: number; label: string; depth: number }> => {
    const list: Array<{ id: number; label: string; depth: number }> = [];
    for (const item of items) {
      if (item.id === excludeId) continue;
      list.push({ id: item.id, label: item.label, depth });
      if (item.children && item.children.length > 0) {
        list.push(...getFlatParents(item.children, depth + 1, excludeId));
      }
    }
    return list;
  };

  const renderItems = (items: NonNullable<typeof menu.data>, depth = 0): JSX.Element[] =>
    items.flatMap((item) => [
      <li
        key={item.id}
        className="flex items-center gap-3 border-b border-ink-100 py-2.5 last:border-0 hover:bg-ink-50/50 rounded-lg px-2 transition"
        style={{ paddingLeft: depth * 24 + 8 }}
      >
        {depth > 0 ? <span className="text-ink-400 font-mono">└─</span> : null}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-semibold text-ink-900">{item.label}</p>
            {item.children && item.children.length > 0 ? (
              <span className="text-[11px] bg-ink-100 text-ink-600 rounded px-1.5 py-0.2">
                {item.children.length} alt öğe
              </span>
            ) : null}
          </div>
          <p className="truncate font-mono text-xs text-ink-500">{item.url ?? '—'}</p>
        </div>
        {item.badgeText ? (
          <span className="badge bg-brand-50 text-brand-800">{item.badgeText}</span>
        ) : null}
        {!item.isActive ? <span className="badge bg-amber-50 text-amber-700 border border-amber-200">Pasif</span> : null}

        {can(P.content.manage) ? (
          <div className="flex gap-1">
            <button
              type="button"
              className="btn-ghost btn-sm text-xs font-medium"
              onClick={() =>
                setEditing({
                  id: item.id,
                  form: {
                    menuKey: item.menuKey,
                    label: item.label,
                    url: item.url,
                    linkType: item.linkType,
                    targetId: item.targetId,
                    parentId: item.parentId,
                    iconUrl: item.iconUrl,
                    badgeText: item.badgeText,
                    openInNewTab: item.openInNewTab,
                    isActive: item.isActive,
                    displayOrder: item.displayOrder,
                  },
                })
              }
            >
              Düzenle
            </button>
            <button
              type="button"
              className="btn-ghost btn-sm text-xs text-red-600 hover:bg-red-50"
              onClick={() => setDeleteId(item.id)}
            >
              Sil
            </button>
          </div>
        ) : null}
      </li>,
      ...renderItems(item.children ?? [], depth + 1),
    ]);

  return (
    <div>
      <Toolbar>
        <div className="flex items-center gap-3">
          <label className="label text-sm font-medium" htmlFor="menu-key">
            Menü:
          </label>
          <select
            id="menu-key"
            className="field w-auto min-w-44 py-2 text-sm"
            value={menuKey}
            onChange={(event) => setMenuKey(event.target.value)}
          >
            <option value="main">Ana Menü (Üst Gezinti)</option>
            <option value="footer">Alt Bilgi Menüsü (Footer)</option>
            <option value="mobile">Mobil Çekmece Menüsü</option>
          </select>
        </div>

        {can(P.content.manage) ? (
          <button
            type="button"
            className="btn-primary ml-auto"
            onClick={() =>
              setEditing({
                id: null,
                form: {
                  menuKey,
                  label: '',
                  url: '',
                  linkType: 'custom',
                  openInNewTab: false,
                  isActive: true,
                  displayOrder: (menu.data ?? []).length,
                },
              })
            }
          >
            + Menü Öğesi Ekle
          </button>
        ) : null}
      </Toolbar>

      <div className="card p-5">
        {menu.isLoading ? (
          <p className="text-sm text-ink-500">Yükleniyor…</p>
        ) : (menu.data ?? []).length === 0 ? (
          <div className="py-12 text-center">
            <p className="text-sm text-ink-500">Bu menüde henüz öğe bulunmuyor.</p>
            {can(P.content.manage) ? (
              <button
                type="button"
                className="btn-secondary btn-sm mt-3"
                onClick={() =>
                  setEditing({
                    id: null,
                    form: {
                      menuKey,
                      label: '',
                      url: '',
                      linkType: 'custom',
                      openInNewTab: false,
                      isActive: true,
                      displayOrder: 0,
                    },
                  })
                }
              >
                İlk Öğeyi Ekle
              </button>
            ) : null}
          </div>
        ) : (
          <ul className="divide-y divide-ink-100">{renderItems(menu.data ?? [])}</ul>
        )}
      </div>

      <Modal
        open={editing !== null}
        title={editing?.id === null ? 'Yeni Menü Öğesi Ekle' : 'Menü Öğesini Düzenle'}
        onClose={() => setEditing(null)}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => save.mutate()}
              disabled={save.isPending || !editing?.form.label.trim()}
            >
              {save.isPending ? <Spinner className="h-4 w-4" /> : null}
              Kaydet
            </button>
          </>
        }
      >
        {editing ? (
          <div className="space-y-4">
            <FormErrors error={save.error} />

            <Field label="Menü Başlığı (Etiket)" htmlFor="mn-label" required hint="Ziyaretçilerin göreceği buton veya bağlantı metni.">
              <input
                id="mn-label"
                className="field"
                placeholder="Örn: Yeni Sezon, İletişim, Kampanyalar"
                value={editing.form.label}
                onChange={(event) =>
                  setEditing((c) => c && { ...c, form: { ...c.form, label: event.target.value } })
                }
              />
            </Field>

            <Field label="Bağlantı Adresi (URL)" htmlFor="mn-url" hint="Sayfa yolu (/urunler) veya harici link (https://...).">
              <input
                id="mn-url"
                className="field font-mono text-sm"
                placeholder="/urunler veya https://instagram.com/..."
                value={editing.form.url ?? ''}
                onChange={(event) =>
                  setEditing((c) => c && { ...c, form: { ...c.form, url: event.target.value } })
                }
              />
              <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs">
                <span className="text-ink-400 mr-1">Hızlı bağlantı:</span>
                {[
                  { name: 'Ana Sayfa', url: '/' },
                  { name: 'Tüm Ürünler', url: '/urunler' },
                  { name: 'Blog', url: '/blog' },
                  { name: 'Sıkça Sorulanlar', url: '/sss' },
                  { name: 'İletişim', url: '/iletisim' },
                  { name: 'Sipariş Takibi', url: '/siparis-takip' },
                ].map((preset) => (
                  <button
                    key={preset.url}
                    type="button"
                    className="rounded bg-ink-100 px-2 py-0.5 text-ink-700 hover:bg-brand-50 hover:text-brand-700 transition"
                    onClick={() =>
                      setEditing((c) => c && {
                        ...c,
                        form: {
                          ...c.form,
                          url: preset.url,
                          label: c.form.label ? c.form.label : preset.name,
                        },
                      })
                    }
                  >
                    {preset.name}
                  </button>
                ))}
              </div>
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field
                label="Üst Menü Öğesi"
                htmlFor="mn-parent"
                hint="Bu öğe başka bir menünün alt menüsü olacaksa seçiniz."
              >
                <select
                  id="mn-parent"
                  className="field"
                  value={editing.form.parentId ?? ''}
                  onChange={(event) =>
                    setEditing((c) =>
                      c
                        ? {
                            ...c,
                            form: {
                              ...c.form,
                              parentId: event.target.value ? Number(event.target.value) : null,
                            },
                          }
                        : null,
                    )
                  }
                >
                  <option value="">-- Ana Seviye (Üst Öğe Yok) --</option>
                  {getFlatParents(menu.data ?? [], 0, editing.id).map((p) => (
                    <option key={p.id} value={p.id}>
                      {'\u00A0'.repeat(p.depth * 3)}{p.depth > 0 ? '└ ' : ''}{p.label}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Görüntülenme Sırası" htmlFor="mn-order" hint="Küçük sayılar önce gösterilir.">
                <input
                  id="mn-order"
                  type="number"
                  className="field"
                  value={editing.form.displayOrder}
                  onChange={(event) =>
                    setEditing(
                      (c) =>
                        c && { ...c, form: { ...c.form, displayOrder: Number(event.target.value) } },
                    )
                  }
                />
              </Field>

              <Field label="Rozet Metni (Opsiyonel)" htmlFor="mn-badge" hint="Örn: Yeni, İndirim, Fırsat">
                <input
                  id="mn-badge"
                  className="field"
                  placeholder="Yeni"
                  value={editing.form.badgeText ?? ''}
                  onChange={(event) =>
                    setEditing(
                      (c) => c && { ...c, form: { ...c.form, badgeText: event.target.value } },
                    )
                  }
                />
              </Field>
            </div>

            <div className="flex flex-wrap gap-6 pt-1">
              <Check
                label="Aktif (Sitede Göster)"
                checked={editing.form.isActive}
                onChange={(value) =>
                  setEditing((c) => c && { ...c, form: { ...c.form, isActive: value } })
                }
              />
              <Check
                label="Yeni Sekmede Aç"
                checked={editing.form.openInNewTab}
                onChange={(value) =>
                  setEditing((c) => c && { ...c, form: { ...c.form, openInNewTab: value } })
                }
              />
            </div>
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={deleteId !== null}
        title="Menü öğesini sil"
        message="Alt öğeleri varsa onlar da kaldırılabilir."
        confirmLabel="Sil"
        danger
        busy={remove.isPending}
        onCancel={() => setDeleteId(null)}
        onConfirm={() => deleteId !== null && remove.mutate(deleteId)}
      />
    </div>
  );
}

/* ==================================== SSS ==================================== */

const emptyFaq: FaqItemUpsertDto = {
  question: '',
  answer: '',
  category: '',
  isActive: true,
  displayOrder: 0,
};

function FaqTab() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const [category, setCategory] = useState('');
  const [editing, setEditing] = useState<{ id: number | null; form: FaqItemUpsertDto } | null>(null);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const faq = useQuery({
    queryKey: ['admin', 'faq', category],
    queryFn: () => adminContentApi.faq(category || undefined),
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'faq'] });

  const save = useMutation({
    mutationFn: () =>
      editing!.id === null
        ? adminContentApi.createFaq(editing!.form)
        : adminContentApi.updateFaq(editing!.id, editing!.form),
    onSuccess: () => {
      toast.success('Soru kaydedildi.');
      setEditing(null);
      void invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => adminContentApi.deleteFaq(id),
    onSuccess: (message) => {
      toast.success(message ?? 'Soru silindi.');
      setDeleteId(null);
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'Silinemedi.'),
  });

  const columns: Column<NonNullable<typeof faq.data>[number]>[] = [
    {
      key: 'question',
      header: 'Soru',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink-900">{row.question}</p>
          <p className="truncate text-xs text-ink-400">{truncate(row.answer.replace(/<[^>]+>/g, ''), 90)}</p>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Kategori',
      hideOnMobile: true,
      cell: (row) => row.category ?? <span className="text-ink-300">—</span>,
    },
    {
      key: 'status',
      header: 'Durum',
      align: 'center',
      cell: (row) =>
        row.isActive ? (
          <span className="badge bg-emerald-50 text-emerald-700">Aktif</span>
        ) : (
          <span className="badge bg-ink-100 text-ink-600">Pasif</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      cell: (row) =>
        can(P.content.manage) ? (
          <div className="flex justify-end gap-1">
            <button
              type="button"
              className="btn-ghost btn-sm"
              onClick={() =>
                setEditing({
                  id: row.id,
                  form: {
                    question: row.question,
                    answer: row.answer,
                    category: row.category,
                    isActive: row.isActive,
                    displayOrder: row.displayOrder,
                    productId: row.productId,
                  },
                })
              }
            >
              Düzenle
            </button>
            <button
              type="button"
              className="btn-ghost btn-sm text-red-600"
              onClick={() => setDeleteId(row.id)}
            >
              Sil
            </button>
          </div>
        ) : null,
    },
  ];

  return (
    <div>
      <Toolbar>
        <SearchInput value={category} onChange={setCategory} placeholder="Kategoriye göre filtrele" />

        {can(P.content.manage) ? (
          <button
            type="button"
            className="btn-primary ml-auto"
            onClick={() => setEditing({ id: null, form: emptyFaq })}
          >
            Yeni soru
          </button>
        ) : null}
      </Toolbar>

      <DataTable
        columns={columns}
        rows={faq.data ?? []}
        rowKey={(row) => row.id}
        isLoading={faq.isLoading}
        error={faq.error}
        onRetry={() => void faq.refetch()}
        emptyMessage="Soru yok."
      />

      <Modal
        open={editing !== null}
        title={editing?.id === null ? 'Yeni soru' : 'Soruyu düzenle'}
        onClose={() => setEditing(null)}
        size="lg"
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setEditing(null)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => save.mutate()}
              disabled={save.isPending || !editing?.form.question}
            >
              {save.isPending ? <Spinner className="h-4 w-4" /> : null}
              Kaydet
            </button>
          </>
        }
      >
        {editing ? (
          <div className="space-y-4">
            <FormErrors error={save.error} />

            <Field label="Soru" htmlFor="fq-question" required>
              <input
                id="fq-question"
                className="field"
                value={editing.form.question}
                onChange={(event) =>
                  setEditing((c) => c && { ...c, form: { ...c.form, question: event.target.value } })
                }
              />
            </Field>

            <Field
              label="Cevap"
              htmlFor="fq-answer"
              required
              hint="Zengin metin editörü ile cevabı biçimlendirebilirsiniz."
            >
              <RichTextEditor
                id="fq-answer"
                value={editing.form.answer}
                onChange={(answer) =>
                  setEditing((c) => c && { ...c, form: { ...c.form, answer } })
                }
                minHeight="min-h-[180px]"
                placeholder="Cevap metnini buraya girin..."
              />
            </Field>

            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Kategori" htmlFor="fq-category">
                <input
                  id="fq-category"
                  className="field"
                  placeholder="Kargo, İade, Ödeme"
                  value={editing.form.category ?? ''}
                  onChange={(event) =>
                    setEditing((c) => c && { ...c, form: { ...c.form, category: event.target.value } })
                  }
                />
              </Field>

              <Field label="Bağlı ürün kimliği" htmlFor="fq-product" hint="Sadece o ürün sayfasında çıkar.">
                <input
                  id="fq-product"
                  type="number"
                  className="field"
                  value={editing.form.productId ?? ''}
                  onChange={(event) =>
                    setEditing(
                      (c) =>
                        c && {
                          ...c,
                          form: {
                            ...c.form,
                            productId: event.target.value ? Number(event.target.value) : null,
                          },
                        },
                    )
                  }
                />
              </Field>
            </div>

            <Check
              label="Aktif"
              checked={editing.form.isActive}
              onChange={(value) =>
                setEditing((c) => c && { ...c, form: { ...c.form, isActive: value } })
              }
            />
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={deleteId !== null}
        title="Soruyu sil"
        message="Soru kalıcı olarak silinir."
        confirmLabel="Sil"
        danger
        busy={remove.isPending}
        onCancel={() => setDeleteId(null)}
        onConfirm={() => deleteId !== null && remove.mutate(deleteId)}
      />
    </div>
  );
}

/* ============================= ILETISIM MESAJLARI ============================= */

function ContactTab() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { can } = useAuth();

  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<ContactMessageStatus | ''>('');
  const [replying, setReplying] = useState<{ id: number; text: string; sendEmail: boolean } | null>(
    null,
  );

  const messages = useQuery({
    queryKey: ['admin', 'contact', page, status],
    queryFn: () =>
      adminContentApi.contactMessages({ page, pageSize: 20, status: status || undefined }),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['admin', 'contact'] });

  const reply = useMutation({
    mutationFn: () =>
      adminContentApi.replyToContact(replying!.id, {
        reply: replying!.text,
        sendEmail: replying!.sendEmail,
      }),
    onSuccess: (message) => {
      toast.success(message ?? 'Yanıt gönderildi.');
      setReplying(null);
      void invalidate();
    },
  });

  const updateStatus = useMutation({
    mutationFn: ({ id, next }: { id: number; next: ContactMessageStatus }) =>
      adminContentApi.updateContactStatus(id, next),
    onSuccess: () => {
      toast.success('Durum güncellendi.');
      void invalidate();
    },
    onError: (error) => toast.fromError(error, 'Durum güncellenemedi.'),
  });

  return (
    <div>
      <Toolbar>
        <SelectFilter
          label="Durum"
          value={status}
          onChange={(value) => {
            setStatus(value as ContactMessageStatus | '');
            setPage(1);
          }}
          options={contactMessageStatusValues.map((value) => ({
            value,
            label: contactStatusLabels[value].text,
          }))}
        />
      </Toolbar>

      {messages.isLoading ? (
        <PageLoader />
      ) : (
        <div className="space-y-4">
          {(messages.data?.items ?? []).map((message) => (
            <article key={message.id} className="card p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-ink-900">
                    {message.subject ?? 'Konusuz mesaj'}
                  </p>
                  <p className="text-xs text-ink-500">
                    {message.fullName} · {message.email}
                    {message.phone ? ` · ${message.phone}` : ''} ·{' '}
                    {formatDateTime(message.createdAt)}
                  </p>
                  {message.relatedOrderNumber ? (
                    <p className="mt-1 text-xs text-ink-500">
                      İlgili sipariş: {message.relatedOrderNumber}
                    </p>
                  ) : null}
                </div>

                <div className="flex flex-wrap items-center gap-2">
                  <Badge label={labelOf(contactStatusLabels, message.status)} />
                  {can(P.content.manage) ? (
                    <>
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        onClick={() =>
                          setReplying({
                            id: message.id,
                            text: message.staffReply ?? '',
                            sendEmail: true,
                          })
                        }
                      >
                        Yanıtla
                      </button>
                      <select
                        className="field w-auto py-1.5 text-xs"
                        value={message.status}
                        aria-label="Durum değiştir"
                        onChange={(event) =>
                          updateStatus.mutate({
                            id: message.id,
                            next: event.target.value as ContactMessageStatus,
                          })
                        }
                      >
                        {contactMessageStatusValues.map((value) => (
                          <option key={value} value={value}>
                            {contactStatusLabels[value].text}
                          </option>
                        ))}
                      </select>
                    </>
                  ) : null}
                </div>
              </div>

              <p className="mt-3 whitespace-pre-line text-sm leading-6 text-ink-700">
                {message.message}
              </p>

              {message.staffReply ? (
                <div className="mt-3 rounded-lg bg-ink-50 px-4 py-3">
                  <p className="text-xs font-medium text-ink-700">
                    Yanıt · {formatDateTime(message.repliedAt)}
                  </p>
                  <p className="mt-1 text-sm text-ink-600">{message.staffReply}</p>
                </div>
              ) : null}
            </article>
          ))}

          {(messages.data?.items ?? []).length === 0 ? (
            <p className="rounded-xl border border-dashed border-ink-200 px-6 py-12 text-center text-sm text-ink-500">
              Mesaj yok.
            </p>
          ) : null}

          {messages.data ? (
            <Pagination
              page={messages.data.page}
              totalPages={messages.data.totalPages}
              totalCount={messages.data.totalCount}
              onChange={setPage}
            />
          ) : null}
        </div>
      )}

      <Modal
        open={replying !== null}
        title="Mesajı yanıtla"
        onClose={() => setReplying(null)}
        footer={
          <>
            <button type="button" className="btn-secondary" onClick={() => setReplying(null)}>
              Vazgeç
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={() => reply.mutate()}
              disabled={reply.isPending || !replying?.text}
            >
              {reply.isPending ? <Spinner className="h-4 w-4" /> : null}
              Gönder
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <FormErrors error={reply.error} />

          <Field label="Yanıt" htmlFor="ct-reply" required>
            <textarea
              id="ct-reply"
              className="field min-h-32"
              value={replying?.text ?? ''}
              onChange={(event) => setReplying((c) => c && { ...c, text: event.target.value })}
            />
          </Field>

          <Check
            label="Müşteriye e-posta gönder"
            checked={replying?.sendEmail ?? true}
            onChange={(value) => setReplying((c) => c && { ...c, sendEmail: value })}
          />
        </div>
      </Modal>
    </div>
  );
}

/* ============================== BULTEN ABONELERI ============================== */

function NewsletterTab() {
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');

  const subscribers = useQuery({
    queryKey: ['admin', 'newsletter', page, search],
    queryFn: () =>
      adminContentApi.newsletterSubscribers({ page, pageSize: 30, search: search || undefined }),
    placeholderData: keepPreviousData,
  });

  const columns: Column<NonNullable<typeof subscribers.data>['items'][number]>[] = [
    {
      key: 'email',
      header: 'E-posta',
      cell: (row) => (
        <div className="min-w-0">
          <p className="truncate text-ink-900">{row.email}</p>
          {row.fullName ? <p className="truncate text-xs text-ink-400">{row.fullName}</p> : null}
        </div>
      ),
    },
    {
      key: 'source',
      header: 'Kaynak',
      hideOnMobile: true,
      cell: (row) => row.source ?? <span className="text-ink-300">—</span>,
    },
    {
      key: 'status',
      header: 'Durum',
      cell: (row) =>
        row.isUnsubscribed ? (
          <span className="badge bg-ink-100 text-ink-600">Ayrıldı</span>
        ) : row.isConfirmed ? (
          <span className="badge bg-emerald-50 text-emerald-700">Onaylı</span>
        ) : (
          <span className="badge bg-amber-50 text-amber-800">Onay bekliyor</span>
        ),
    },
    {
      key: 'date',
      header: 'Kayıt',
      hideOnMobile: true,
      cell: (row) => <span className="text-ink-500">{formatDate(row.createdAt)}</span>,
    },
  ];

  return (
    <div>
      <Toolbar>
        <SearchInput
          value={search}
          onChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          placeholder="E-posta ara"
        />
      </Toolbar>

      <DataTable
        columns={columns}
        rows={subscribers.data?.items ?? []}
        rowKey={(row) => row.id}
        isLoading={subscribers.isLoading}
        error={subscribers.error}
        onRetry={() => void subscribers.refetch()}
        emptyMessage="Abone yok."
        footer={
          subscribers.data ? (
            <Pagination
              page={subscribers.data.page}
              totalPages={subscribers.data.totalPages}
              totalCount={subscribers.data.totalCount}
              onChange={setPage}
            />
          ) : null
        }
      />
    </div>
  );
}
